'use client'

import { useDisableScrollLock } from '@/hooks/useDisableScrollLock'
import { useEffect, useMemo, useState } from 'react'

export type UseIframeScrollLockOptions = {
  enableFocus?: boolean
  enableHover?: boolean
}

export type IframeHandlers = {
  onFocus?: () => void
  onBlur?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export type UseIframeScrollLockReturn = {
  handlers: IframeHandlers
}

/**
 * Safe and optimized hook for managing scroll lock during iframe interactions
 * Handles missing provider gracefully for maximum compatibility
 *
 * @param {UseIframeScrollLockOptions} options - Configuration options
 * @returns {UseIframeScrollLockReturn} Object containing event handlers and interaction state
 */
export const useIframeScrollLock = (
  options: UseIframeScrollLockOptions = {},
  setScrollLock?: (isLocked: boolean) => void,
): UseIframeScrollLockReturn => {
  const { enableFocus = true, enableHover = true } = options
  const [isIframeInteracted, setIsIframeInteracted] = useState(false)
  const disableScrollLock = useDisableScrollLock()

  // Manage scroll lock state only if context exists
  useEffect(() => {
    if (disableScrollLock) {
      return
    }

    if (setScrollLock) {
      setScrollLock(isIframeInteracted)

      // Cleanup function: ensure scroll lock is released when component unmounts
      return () => {
        setScrollLock(false)
      }
    }
  }, [isIframeInteracted, setScrollLock, disableScrollLock])

  // Optimize handlers object creation using useMemo to avoid unnecessary re-creation
  const handlers = useMemo((): IframeHandlers => {
    const result: IframeHandlers = {}

    if (!setScrollLock || disableScrollLock) {
      return result
    }

    if (enableFocus) {
      result.onFocus = () => setIsIframeInteracted(true)
      result.onBlur = () => setIsIframeInteracted(false)
    }

    if (enableHover) {
      result.onMouseEnter = () => setIsIframeInteracted(true)
      result.onMouseLeave = () => setIsIframeInteracted(false)
    }

    return result
  }, [enableFocus, enableHover, setScrollLock, disableScrollLock])

  return { handlers }
}
